import React from 'react';
import CTASection from '../components/CTASection';
import Reveal from '../components/Reveal';
import { markets, processSteps, products, routeHref, standards } from '../data/siteData';

const HomePage = () => {
  return (
    <main>
      <section className="page-hero">
        <div className="container-custom">
          <div className="page-hero-grid">
            <Reveal>
              <p className="eyebrow-pill mb-6">S4CoconutExports</p>
              <h1 className="page-hero-title">Coconut exports for serious global buyers.</h1>
              <p className="page-hero-copy">
                Fresh coconuts and coconut-based export options sourced, checked, graded, packed, documented, and coordinated for importers, distributors, wholesalers, and food businesses.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a className="btn-primary" href={routeHref('/products')}>Explore Products</a>
                <a className="btn-secondary" href={routeHref('/process')}>View Export Process</a>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="hero-editorial-visual">
                <img src="/media/farm-image.jpg" alt="Coconut farm prepared for sourcing" />
                <div className="hero-editorial-note">
                  <span>Farm to port</span>
                  <strong>Structured supply for coconut import and wholesale teams.</strong>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="container-custom">
          <Reveal>
            <div className="brand-intro-strip">
              <p className="eyebrow-pill">Buyer-focused export partner</p>
              <h2>Coconut supply becomes easier when sourcing, quality checks, packaging, documents, and shipment communication move as one disciplined workflow.</h2>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="journey-ribbon mt-10">
              {processSteps.slice(0, 6).map((step) => (
                <div key={step.title} className="journey-ribbon-item">
                  <span>{step.number}</span>
                  <p>{step.title.replace(' & ', ' + ')}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="page-section section-rule">
        <div className="container-custom">
          <div className="editorial-split">
            <Reveal>
              <div>
                <p className="eyebrow-pill mb-5">Product range</p>
                <h2 className="section-title mb-5">A focused coconut portfolio for trade clarity.</h2>
                <p className="section-subtitle max-w-xl">
                  Buyers can review fresh coconuts, semi-husked coconuts, coconut oil, copra, coconut powder, and desiccated coconut with practical notes on use case, packing, and quality expectations.
                </p>
                <a className="text-link mt-7" href={routeHref('/products')}>Explore product capability</a>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="product-preview-stack">
                {products.slice(0, 4).map((product) => (
                  <a href={routeHref('/products')} className="product-preview-row" key={product.name}>
                    <img src={product.image} alt={product.name} />
                    <div>
                      <h3>{product.name}</h3>
                      <p>{product.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="page-section section-rule">
        <div className="container-custom">
          <div className="preview-lanes">
            <Reveal>
              <article className="preview-lane preview-lane-large">
                <span>Process preview</span>
                <h2>From farm selection to buyer delivery support.</h2>
                <p>Each stage is designed to reduce uncertainty for importers: source carefully, inspect clearly, prepare responsibly, and keep shipment details organized.</p>
                <a className="text-link" href={routeHref('/process')}>Follow the export journey</a>
              </article>
            </Reveal>

            <Reveal delay={100}>
              <article className="preview-lane">
                <span>Quality preview</span>
                <h3>{standards[0].title}</h3>
                <p>{standards[0].text}</p>
                <a className="text-link" href={routeHref('/quality')}>Review standards</a>
              </article>
            </Reveal>

            <Reveal delay={180}>
              <article className="preview-lane">
                <span>Global preview</span>
                <h3>Built for importers and bulk buyers.</h3>
                <p>Market conversations can be shaped around destination, product format, packing expectations, and recurring supply planning.</p>
                <a className="text-link" href={routeHref('/global')}>View global reach</a>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="page-section section-rule">
        <div className="container-custom">
          <div className="market-marquee" aria-label="Market categories preview">
            {markets.map((market) => (
              <span key={market.name}>{market.name}</span>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Share your coconut requirement and destination details."
        text="Our team can respond with suitable product options, packing discussion, quantity planning, and export-ready next steps."
        primaryLabel="Start an Inquiry"
        secondaryLabel="View Products"
        secondaryPath="/products"
      />
    </main>
  );
};

export default HomePage;
